import { useCallback } from 'react'
import { UserFileRead } from '@/uppy-file-upload/schemas'
import { UserFileProcessingBlockedReason, UserFileProcessingStatus } from '@/user_files/schemas/processing-status'
import { FileOverflowMenu } from './FileOverflowMenu'
import { DriveListItem } from '@/user_files/drive/components/drive-list-item/DriveListItem'
import { DriveListItemStatus } from '@/user_files/drive/constants'
import { fileProcessingStatusToDriveStatus, formatDriveListUpdatedAtParts, formatFileSize } from '@/user_files/utils/drive-list-format'
import { useViewUserFile } from '@/user_files/files/hooks/useViewUserFile'
import { downloadFile } from '@/user_files/files/fetch/download-user-file'
import { useAppDispatch } from '@/store/store-hooks'
import { openGlobalToast } from '@/store/slices/global-toast.slice'
import { GlobalToastType, kToastDurationMediumMs, kToastDurationShortMs } from '@/constants/constants-ui'
import { UserFileTypeIcon } from './UserFileTypeIcon'
import { isLocalStorageItem, isMatterFilesDeleteEligible } from '@/user_files/external-storage/utils/external-storage-items'
import { openInProviderTab, resolveOpenInProviderFile } from '@/user_files/external-storage/utils/open-in-provider'
import { EmailAttachmentDisclosure } from '@/user_files/components/EmailAttachmentDisclosure'

interface FileListItemProps {
  file: UserFileRead
  attachmentDepth?: number
  attachmentParentName?: string
  /** Visible parent label for flat search hits; browse tree keeps parent context sr-only. */
  showAttachmentParentLabel?: boolean
  hasExtractedChildren?: boolean
  /** Extracted descendant count for the email attachment label beside the filename. */
  extractedAttachmentCount?: number
  attachmentsExpanded?: boolean
  onToggleAttachments?: () => void
  /** Containing folder name, shown only in the search results table. */
  locationLabel?: string
  /** Whole trail from the drive root, shown on hover over the Location cell. */
  locationPath?: string
  /** Set for a synced row: no selection, no mutations, and the note explaining why. */
  readOnlyNote?: string
  disabled?: boolean
  selected: boolean
  onToggleSelect: () => void
  onRename?: () => void
  onDelete: () => void
  onMove?: () => void
  onRetry?: () => void
}

/**
 * FileListItem
 *
 * Single file row in the drive list. Uses shared DriveListItem.
 */
export function FileListItem({
  file,
  attachmentDepth,
  attachmentParentName,
  showAttachmentParentLabel,
  hasExtractedChildren = false,
  extractedAttachmentCount = 0,
  attachmentsExpanded = false,
  onToggleAttachments,
  locationLabel,
  locationPath,
  disabled,
  selected,
  onToggleSelect,
  onRename,
  onDelete,
  onMove,
  onRetry,
  readOnlyNote,
}: FileListItemProps) {
  const dispatch = useAppDispatch()
  const viewUserFile = useViewUserFile()
  const isTerminal = file.processing_status === UserFileProcessingStatus.Completed || file.processing_status === UserFileProcessingStatus.Failed
  // A queued file has no processed document yet, but its upload can still be selected, downloaded, and deleted.
  const isQueued = !isTerminal && file.processing_blocked_reason === UserFileProcessingBlockedReason.OcrQuota
  const status = isQueued ? DriveListItemStatus.Queued : fileProcessingStatusToDriveStatus(file.processing_status)
  const isProcessing = status === DriveListItemStatus.Processing || status === DriveListItemStatus.Pending
  const errorMessage = status === DriveListItemStatus.Failed ? file.processing_error ?? 'Processing Failed' : undefined
  const { date, time } = formatDriveListUpdatedAtParts(file.updated_at)
  const isReady = file.processing_status === UserFileProcessingStatus.Completed
  const statusMessage = isQueued ? 'Waiting for OCR page quota' : undefined
  const isExternal = !isLocalStorageItem(file)
  const isNestedFile = Boolean(file.extracted_from_file_id)
  const canDelete = isMatterFilesDeleteEligible(file)
  const openInProvider = resolveOpenInProviderFile(file.external_storage)
  const handleOpenInProvider = openInProvider ? () => openInProviderTab(openInProvider.url) : undefined
  const showDisclosure = hasExtractedChildren && Boolean(onToggleAttachments)

  const handleDownload = useCallback(async () => {
    try {
      await downloadFile({
        fileId: file.id,
        storagePath: file.storage_path,
        fileName: file.original_filename,
      })
      dispatch(
        openGlobalToast({
          type: GlobalToastType.SUCCESS,
          message: `${file.original_filename} downloaded successfully`,
          durationMs: kToastDurationShortMs,
        })
      )
    } catch {
      dispatch(
        openGlobalToast({
          type: GlobalToastType.ERROR,
          message: `${file.original_filename} download failed`,
          durationMs: kToastDurationMediumMs,
        })
      )
    }
  }, [dispatch, file.id, file.original_filename, file.storage_path])

  return (
    <DriveListItem
      variant="file"
      status={status}
      name={file.original_filename}
      nameIndentLevel={attachmentDepth}
      attachmentParentName={attachmentParentName}
      showAttachmentParentLabel={showAttachmentParentLabel}
      attachmentCount={extractedAttachmentCount}
      disclosure={
        showDisclosure ? (
          <EmailAttachmentDisclosure filename={file.original_filename} expanded={attachmentsExpanded} onToggle={onToggleAttachments!} />
        ) : undefined
      }
      formattedDate={date}
      formattedTime={time}
      detailsLabel={file.size_bytes != null ? formatFileSize(file.size_bytes) : undefined}
      locationLabel={locationLabel}
      locationPath={locationPath}
      selectionDisabledReason={readOnlyNote}
      errorMessage={errorMessage}
      statusMessage={statusMessage}
      // Keep the checkbox cell: this table's header always has a select column, so omitting the
      // cell would shift every following cell one column left. Grouped attachments stay selectable
      // for Ask Assistant; mutations are gated in the toolbar and overflow menu.
      disabled={disabled || !canDelete || isProcessing}
      selected={selected}
      onToggleSelect={onToggleSelect}
      onClick={isReady ? () => viewUserFile(file) : undefined}
      onRetry={status === DriveListItemStatus.Failed ? onRetry : undefined}
      fileIcon={
        <UserFileTypeIcon
          detectedContentType={file.detected_content_type}
          detectedExtension={file.detected_extension}
          originalFilename={file.original_filename}
          hasExtractedAttachments={hasExtractedChildren || extractedAttachmentCount > 0}
          className="w-6 h-6 shrink-0 text-brand-neutral-950"
        />
      }
      overflowMenu={
        // Grouped files are managed through their parent, including when search shows flat rows.
        isNestedFile ? (
          <FileOverflowMenu onDownload={handleDownload} disabled={isProcessing} />
        ) : isExternal ? (
          <FileOverflowMenu
            externalProvider={openInProvider?.provider}
            onOpen={handleOpenInProvider}
            onDownload={isProcessing ? undefined : handleDownload}
            onDelete={canDelete && !isProcessing ? onDelete : undefined}
            footerNote={readOnlyNote}
            disabled={isProcessing && !openInProvider}
          />
        ) : (
          <FileOverflowMenu
            onDownload={handleDownload}
            onRename={isQueued ? undefined : onRename}
            onDelete={onDelete}
            onMove={isQueued ? undefined : onMove}
            disabled={isProcessing}
          />
        )
      }
    />
  )
}
